import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Flex, Input } from 'antd';
import React from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { Button, Typography } from 'tfc-components';

import { PackageForm } from '.';

interface Props {
  total: number;
  duration: 'monthly' | 'yearly';
  billingType: 'one-time' | 'subscription';
  readonly?: boolean;
  visiblePackages: {
    id: string;
    name: string;
    price: number;
    duration: number;
    quantity: number;
  }[];
  width?: string;
  method?: UseFormReturn<PackageForm>;
  isConfirm?: boolean;
  discountData?: {
    discountAmount: number;
    discountDay: number;
    servicePackageAddOns: {
      discountAmount?: number;
      maxQty?: number;
      quantity: number;
      servicePackageAddOnId: number;
      unit: number;
    }[];

  } | null;
  packageQty?: number;
}

const ChoosePackage: React.FC<Props> = ({
  billingType,
  total,
  duration,
  readonly,
  visiblePackages,
  width,
  method,
  isConfirm,
  discountData,
  packageQty,
}) => {
  const handleInputValue = (id: string, value: number) => {
    if (!id) return value;
    let newValue = value;

    if (duration === 'monthly' && billingType === 'one-time' && newValue < 3) {
      newValue = 3;
    }

    if (duration === 'yearly' && billingType === 'one-time') {
      newValue = 1; // luôn 1
    }

    if (billingType === 'subscription' && newValue > packageQty!) {
      newValue = packageQty!;
    }

    return newValue;
  };

  const isEmpty = visiblePackages.length === 0;

  // lấy luôn giá trị trong form để đồng bộ readonly / editable
  const watchedPackages = useWatch({
    control: method?.control,
    name: 'packages',
  });

  return (
    <div className="p-checkout_productWrap">
      <Typography.Text fontweight="600" extendClasses="p-checkout_titleProduct fs-20x24">
        Product list
      </Typography.Text>

      <div className="p-checkout_table">
        {/* Header */}
        <div className={isConfirm ? 'p-addNewPackage_tableHead' : 'p-checkout_table_head'}>
          <div>
            <Typography.Text fontweight="600">Name</Typography.Text>
          </div>
          <div>
            <Typography.Text fontweight="600">
              Unit
            </Typography.Text>
          </div>
          <div>
            <Typography.Text fontweight="600">
              Duration (
              {duration === 'monthly' ? 'month' : 'year'}
              )
            </Typography.Text>
          </div>
          <div>
            <Typography.Text fontweight="600">Base price (VND)</Typography.Text>
          </div>
          <div>
            <Typography.Text fontweight="600">Total price (VND)</Typography.Text>
          </div>
        </div>

        {/* Rows */}
        {visiblePackages?.length > 0 ? (
          visiblePackages.map((pkg, index) => {
            const { id, name, price } = pkg;
            const currentPkg = watchedPackages?.[index];
            const quantity = currentPkg?.quantity ?? pkg.quantity;
            const durationValue = currentPkg?.duration ?? pkg.duration;
            const totalPrice = price * (quantity ?? 1) * (billingType === 'subscription' ? 1 : pkg?.duration ?? 1);
            const discount = discountData?.servicePackageAddOns.find(
              (item) => item.servicePackageAddOnId?.toString() === pkg?.id && item.discountAmount
            );

            return (
              <>
                <div
                  className={
                    isConfirm
                      ? 'p-addNewPackage_tableHead'
                      : 'p-checkout_table_head'
                  }
                  key={id}
                >
                  {/* name */}
                  <div>
                    <Typography.Text>{name || '--'}</Typography.Text>
                  </div>

                  {/* quantity */}
                  <div className="p-checkout_table_quantity">
                    {!readonly ? (
                      <Controller
                        control={method?.control}
                        name={`packages.${index}.quantity`}
                        render={({ field }) => (
                          <>
                            <Button
                              disabled={field.value <= 1}
                              extendClasses="p-checkout_table_button"
                              onClick={() => field.onChange(field.value - 1)}
                            >
                              <MinusOutlined />
                            </Button>
                            <Input
                              className="p-checkout_table_input"
                              style={{ width: '70px', textAlign: 'center' }}
                              value={field.value}
                              onChange={(e) => field.onChange(
                                handleInputValue(
                                  id,
                                  Number(e.target.value) || 0
                                )
                              )}
                            />
                            <Button
                              extendClasses="p-checkout_table_button"
                              onClick={() => field.onChange(field.value + 1)}
                            >
                              <PlusOutlined />
                            </Button>
                          </>
                        )}
                      />
                    ) : (
                      <Typography.Text extendClasses="p-checkout_table_inputText">
                        {isEmpty ? '--' : quantity}
                      </Typography.Text>
                    )}
                  </div>

                  {/* duration */}
                  <div className="p-checkout_table_quantity">
                    {!readonly ? (
                      <div>
                        {billingType === 'subscription' ? (
                          <Typography.Text>
                            {duration === 'monthly' ? 'Month' : 'Year'}
                          </Typography.Text>
                        ) : (
                          <Controller
                            control={method?.control}
                            name={`packages.${index}.duration`}
                            render={({ field }) => (
                              <Flex>
                                <Button
                                  disabled={field.value <= 1}
                                  extendClasses="p-checkout_table_button"
                                  onClick={() => field.onChange(field.value - 1)}
                                >
                                  <MinusOutlined />
                                </Button>
                                <Input
                                  className="p-checkout_table_input"
                                  style={{ width: '70px', textAlign: 'center' }}
                                  value={field.value}
                                  onChange={(e) => field.onChange(
                                    handleInputValue(
                                      id,
                                      Number(e.target.value) || 0
                                    )
                                  )}
                                  max={packageQty}
                                />
                                <Button
                                  disabled={field.value >= packageQty!}
                                  extendClasses="p-checkout_table_button"
                                  onClick={() => {
                                    if (field.value < packageQty!) {
                                      field.onChange(field.value + 1);
                                    }
                                  }}
                                >
                                  <PlusOutlined />
                                </Button>
                              </Flex>
                            )}
                          />
                        )}
                      </div>
                    ) : (
                      <div>
                        {billingType === 'subscription' ? (
                          <Typography.Text>
                            {duration === 'monthly' ? 'Month' : 'Year'}
                          </Typography.Text>
                        ) : (
                          <Typography.Text extendClasses="p-checkout_table_inputText">
                            {isEmpty ? '--' : durationValue}
                          </Typography.Text>
                        )}
                      </div>

                    )}
                  </div>

                  {/* price */}
                  <div>
                    <Typography.Text>
                      {isEmpty ? '--' : price?.toLocaleString('vi')}
                    </Typography.Text>
                  </div>

                  {/* total */}
                  <div style={{ width }} className="p-checkout_table_totalValue">
                    <Typography.Text>
                      {isEmpty ? '--' : totalPrice.toLocaleString('vi')}
                    </Typography.Text>
                  </div>
                </div>

                {discount && discount?.discountAmount && discount?.discountAmount > 0 && (
                  <div
                    className={
                      isConfirm ? 'p-changePlan_tableHead' : 'p-checkout_table_row'
                    }
                  >
                    <Flex style={{ paddingLeft: 20 }} gap={4} align="center">
                      <Typography.Text extendClasses="">Discount</Typography.Text>
                    </Flex>

                    <div />
                    <div />
                    <div />
                    <Typography.Text
                      extendClasses="p-checkout_table_totalValue"
                      style={{ width }}
                    >
                      {`- ${discount?.discountAmount?.toLocaleString('vi')}`}
                    </Typography.Text>
                  </div>
                )}
              </>
            );
          })
        ) : (
          <div
            className={
              isConfirm ? 'p-addNewPackage_tableHead' : 'p-checkout_table_head'
            }
          >
            <div>--</div>
            <div>--</div>
            <div>--</div>
            <div>--</div>
            <div>--</div>
          </div>
        )}

        {/* Footer total */}
        <div
          className={`${isConfirm ? 'p-addNewPackage_tableHead' : 'p-checkout_table_head'
            } p-checkout_table_total`}
        >
          <Typography.Text fontweight="600" extendClasses="p-checkout_table_totalLabel">
            Total
          </Typography.Text>
          <div />
          <div />
          <div />
          <Typography.Text
            fontweight="600"
            extendClasses="p-checkout_table_totalValue"
            style={{ width }}
          >
            {isEmpty ? 0 : total.toLocaleString('vi')}
          </Typography.Text>
        </div>
      </div>
    </div>
  );
};

export default ChoosePackage;
